// Branding: the site's name, sign-in message, accent and logo, as an admin
// set them (Administration → Branding), in place of Lantern's own. The server
// writes them into the page, so they show before signing in; a change comes
// with the workspace's, and setBranding shows it at once.
import { useSyncExternalStore } from "react";
import { resetFavicon } from "./favicon";
import { fromBrand } from "./theme";

export interface Branding {
  site_name: string;
  sign_in_message: string;
  // accent is "#rrggbb", or "" for the theme's.
  accent: string;
  // logo_version names the logo; 0 is Lantern's.
  logo_version: number;
}

const lantern: Branding = { site_name: "", sign_in_message: "", accent: "", logo_version: 0 };

function fromPage(): Branding {
  try {
    const raw = document.getElementById("branding")?.textContent;
    return raw ? { ...lantern, ...(JSON.parse(raw) as Partial<Branding>) } : lantern;
  } catch {
    return lantern;
  }
}

let current = typeof document === "undefined" ? lantern : fromPage();
const listeners = new Set<() => void>();

export const branding = () => current;

// siteName is the admin's name for the site, or Lantern.
export const siteName = (b: Branding = current) => b.site_name || "Lantern";

// logo is the link to the logo at a size: the admin's, or Lantern's.
export const logo = (size: 192 | 512, b: Branding = current) =>
  b.logo_version ? `/api/v1/branding/icon-${size}.png?v=${b.logo_version}` : `/icon-${size}.png`;

// setBranding shows new branding: the icons, the accent and, through those
// who use it, the name.
export function setBranding(b: Branding) {
  const next = { ...lantern, ...b };
  // The same again (another change to the workspace) changes nothing, so a
  // form showing it is not reset.
  if ((Object.keys(lantern) as (keyof Branding)[]).every((k) => next[k] === current[k])) return;
  const logoChanged = next.logo_version !== current.logo_version;
  current = next;
  if (logoChanged) {
    for (const el of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')) el.href = logo(192);
    resetFavicon();
  }
  fromBrand(current.accent);
  listeners.forEach((l) => l());
}

export function useBranding(): Branding {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}

// The checks web/src/theme.test.ts makes of Lantern's themes, which the
// server makes of an accent too (identity.ReadableAccent).
const luminance = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a: string, b: string) => {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
};
// mix is CSS's color-mix(in srgb, a p, b).
export const mix = (a: string, b: string, p: number) =>
  "#" + [1, 3, 5].map((i) => Math.round(parseInt(a.slice(i, i + 2), 16) * p + parseInt(b.slice(i, i + 2), 16) * (1 - p)).toString(16).padStart(2, "0")).join("");

// readableAccent says whether white text reads on an accent, and it as text
// on light and dark pages and on a tint of itself, all at 4.5:1 or more.
export function readableAccent(hex: string): boolean {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return false;
  const c = hex.toLowerCase();
  const light = "#ffffff";
  const dark = "#1a1d21";
  const darkText = mix(c, "#ffffff", 0.62);
  return [
    ["#ffffff", c],
    ["#ffffff", mix(c, "#000000", 0.86)],
    [c, light],
    [darkText, dark],
    [mix(c, "#000000", 0.8), mix(c, light, 0.11)],
    [darkText, mix(c, dark, 0.11)],
  ].every(([a, b]) => contrast(a, b) >= 4.5);
}
